<template>
  <div class="login-modal-inner">

    <!-- Branded Header -->
    <div class="login-brand-header">
      <img src="../../icons/Logo.svg" alt="Renants" class="login-brand-logo" />
      <p class="login-brand-tagline">Your local community marketplace</p>
    </div>

    <!-- Form Body -->
    <div class="login-form-body">
      <p class="title">Welcome back</p>
      <p class="subtitle">Sign in to your Renants account</p>

      <label>Email</label>
      <input type="email" v-model="email" placeholder="Enter your email" />

      <label>Password</label>
      <div class="password-wrapper">
        <input
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          placeholder="Enter your password"
        />
        <span class="toggle-password" @click="togglePassword">
          <svg v-if="showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </span>
      </div>

      <div class="login-links">
        <label class="remember-label">
          <input type="checkbox" v-model="remember" />
          <span>Remember for 30 days</span>
        </label>
        <span class="forgot" @click="$emit('switchMode', 'forgot')">Forgot Password?</span>
      </div>

      <button class="btn-signin" :disabled="busy" @click="handleLogin">
        <span v-if="loading" class="btn-spinner" aria-hidden="true"></span>
        {{ loading ? "Signing in…" : "Sign In" }}
      </button>

      <div class="divider"><span></span><p>or continue with</p><span></span></div>

      <!-- Social Icon Buttons -->
      <div class="social-icons-row">
        <button class="btn-social-icon btn-google-icon" :disabled="busy" @click="handleGoogleLogin" title="Continue with Google">
          <span v-if="googleLoading" class="btn-spinner btn-spinner-dark" aria-hidden="true"></span>
          <svg v-else width="32" height="32" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
        </button>
        <!-- <button class="btn-social-icon btn-facebook-icon" @click="handleFacebookLogin" title="Continue with Facebook">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </button> -->
        <button class="btn-social-icon btn-email-icon" :disabled="busy" @click="$emit('switchMode', 'emailLink')" title="Login with Email link">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
        </button>
      </div>

      <div class="switch-box">
        <span>Don't have an account?</span>
        <span class="forgot" @click="$emit('switchMode', 'register')">Create New Account</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { toast } from "vue-sonner";
import { UAParser } from "ua-parser-js";
import { trackEvent, setUserId } from "@/composables/useAnalytics";
import { API_ORIGIN, IS_NATIVE } from "@/config/apiBase";
import { openExternal } from "@/utils/native";
import { getRecaptchaToken, preloadRecaptcha } from "@/utils/recaptcha";
import { trackInteraction } from "@/utils/trackInteraction";
import { trackLogin, prefetchIp } from "@/utils/trackPageVisit";
export default {
  data() {
    return {
      email: "",
      password: "",
      remember: false,
      showPassword: false,
      // Guards against double-submits: `loading` covers the email sign-in,
      // `googleLoading` covers the redirect to Google. While either is set every
      // button in the modal is disabled so a second click can't fire.
      loading: false,
      googleLoading: false,
    };
  },
  props: {
    show: Boolean,
  },
  computed: {
    busy() {
      return this.loading || this.googleLoading;
    },
  },
  mounted() {
    // Track that the login popup opened, and on which page — the current path
    // (label) tells us which listing/deal/more-category/page prompted the login.
    trackInteraction('auth', 'login-open', { label: this.$route?.fullPath || '' });
    // Warm up what Sign In waits on, while the user is still typing.
    preloadRecaptcha();
    prefetchIp();
    this.trackVisitor();
    this.loadGoogleAnalytics();
  },
  methods: {
    async trackVisitor() {
      const parser = new UAParser();
      const browserInfo = parser.getResult();
      const visitData = {
        timestamp: new Date().toISOString(),
        browser: browserInfo.browser.name,
        browserVersion: browserInfo.browser.version,
        os: browserInfo.os.name,
        osVersion: browserInfo.os.version,
        device: browserInfo.device.type || "Desktop",
        pageName: "Login Page",
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      try {
        const response = await fetch("https://api64.ipify.org?format=json");
        const { ip } = await response.json();
        visitData.ip = ip;
        await fetch("/api/visitors", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(visitData),
        });
      } catch (error) {
        console.error("Error tracking visitor:", error);
      }
    },
    loadGoogleAnalytics() {
      const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
      if (!gaId) return Promise.resolve();
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);
      return new Promise((resolve, reject) => {
        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          window.gtag = (...args) => window.dataLayer.push(args);
          window.gtag("js", new Date());
          window.gtag("config", gaId);
          resolve();
        };
        script.onerror = () => {
          console.error("Failed to load Google Analytics.");
          reject(new Error("GA script load failed"));
        };
      });
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    async handleLogin() {
      if (this.busy) return;
      if (!this.email) { toast.error("Please enter your email."); return; }
      if (!this.password) { toast.error("Please enter your password."); return; }
      this.loading = true;
      // Track the Sign-In click (email) with the page it happened on.
      trackInteraction('auth', 'login-submit', { label: 'email', extra: { page: this.$route?.fullPath || '' } });
      try {
        const recaptchaToken = await getRecaptchaToken('login');
        const authRes = await axios.post("/api/authenticate", {
          email: this.email,
          secKey: this.password,
          recaptchaToken,
        });
        if (authRes.data && authRes.status === 200) {
          toast.success("Login successful 🎉");
          localStorage.setItem("auth-token", authRes.data.token);
          const userid = authRes.data.userID;
          const username = authRes.data.userName;
          setUserId(userid);
          trackInteraction('auth', 'login-success', { label: 'email', role: 'user', extra: { userId: userid } });
          trackEvent('login', { method: 'email' });
          const logintime = new Date().toISOString();
          localStorage.setItem("userid", userid);
          localStorage.setItem("username", username || "");
          localStorage.setItem("logintime", logintime);
          // Independent of each other, so run them together rather than in turn.
          // trackLogin resolves once its keepalive POST is on the wire.
          const [userlogRes] = await Promise.all([
            axios.post("/api/userlog", {
              userID: userid,
              loginTime: logintime,
              status: "active",
            }),
            axios.put("/api/updateoneuser", { userID: userid, status: "active" }),
            trackLogin("Email", { userID: userid, userName: username }),
          ]);
          const userlogid = userlogRes.data.item.userlogId;
          localStorage.setItem("userlogid", userlogid);
          const currentPath = this.$route.fullPath;
          const target = localStorage.getItem("redirectTo");
          localStorage.removeItem("redirectTo");
          this.$emit("close");
          if (target && target !== currentPath) {
            await this.$router.push(target);
          }
          // Refresh the page in place as the signed-in user (App.vue remounts the
          // routed view) instead of a full window.location.reload().
          window.dispatchEvent(new Event("renants:logged-in"));
        } else {
          toast.error("Invalid email or password");
          this.loading = false;
        }
      } catch (err) {
        toast.error("Login failed! Please try again");
        this.loading = false;
      }
      // On success we deliberately leave `loading` set — the page is reloading or
      // routing away, and the button must stay disabled until it does.
    },
    handleGoogleLogin() {
      if (this.busy) return;
      this.googleLoading = true;
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      // In dev mode, frontend (Vite) and backend (Express) are on different ports
      // Use backend URL for OAuth redirect so the callback reaches the Express server
      const isDev = import.meta.env.DEV;
      // Native app: window.location.origin is capacitor://localhost — use the
      // real backend origin. (Phase 3 replaces this flow with native sign-in.)
      const backendOrigin = isDev ? 'http://localhost:4000' : (API_ORIGIN || window.location.origin);
      const redirectUri = backendOrigin + '/auth/google/callback';
      const scope = 'profile email';
      
      // Store the current path so we can redirect back after login
      const currentPath = this.$route?.fullPath || window.location.pathname;
      if (currentPath && currentPath !== '/') {
        localStorage.setItem('redirectTo', currentPath);
      }
      
      // Build OAuth URL and redirect the main window (no popup). state=native
      // tells the backend callback to redirect to the app's deep link
      // (com.renants.app://auth) instead of the web origin.
      const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&prompt=select_account${IS_NATIVE ? '&state=native' : ''}`;

      trackInteraction('auth', 'login-submit', { label: 'google', extra: { page: currentPath } });
      trackEvent('login', { method: 'google' });
      if (IS_NATIVE) {
        // In-app browser (SFSafariViewController / Custom Tabs) — the deep-link
        // listener in App.vue closes it when the token arrives. Navigating the
        // WebView itself would bounce the user out to Safari with no way back.
        openExternal(oauthUrl);
        this.googleLoading = false;
        return;
      }
      // Redirect the main window to Google OAuth
      window.location.href = oauthUrl;
    },
    handleFacebookLogin() {
      trackEvent('login', { method: 'facebook' });
      const facebookAppId = import.meta.env.VITE_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
      const redirectUri = window.location.origin + '/auth/facebook/callback';
      const scope = 'email,public_profile';
      const oauthUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${facebookAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&auth_type=rerequest`;
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(oauthUrl, 'FacebookLogin', `width=${width},height=${height},left=${left},top=${top}`);
      let handled = false;
      const messageHandler = async (event) => {
        if (event.origin !== window.location.origin || handled) return;
        handled = true;
        if (event.data && event.data.type === 'facebook-auth-success') {
          localStorage.setItem('auth-token', event.data.token);
          try {
            const userRes = await axios.post('/api/getoneusers', {}, { headers: { Authorization: `Bearer ${event.data.token}` } });
            if (userRes.data && userRes.data.item) {
              localStorage.setItem('userid', userRes.data.item.userID);
              localStorage.setItem('username', userRes.data.item.userName);
              localStorage.setItem('email', userRes.data.item.email);
              await trackLogin('Facebook', { userID: userRes.data.item.userID, userName: userRes.data.item.userName });
            }
          } catch (e) {}
          this.$emit('close');
          const target = localStorage.getItem('redirectTo');
          if (target) { this.$router.push(target); localStorage.removeItem('redirectTo'); } else { this.$router.push('/'); }
        } else if (event.data && event.data.type === 'auth-error') {
          toast.error(event.data.message || 'Login failed');
          this.$emit('close');
        }
        window.removeEventListener('message', messageHandler);
      };
      window.addEventListener('message', messageHandler);
      setTimeout(() => { if (!handled) { handled = true; toast.error('Login timed out. Please try again.'); this.$emit('close'); window.removeEventListener('message', messageHandler); } }, 60000);
    },
  },
};
</script>

<style scoped>
/* ── Brand Header ── */
.login-brand-header {
  background: linear-gradient(135deg, #1d3fa8 0%, #3467d5 60%, #5b87f0 100%);
  border-radius: 14px 14px 0 0;
  padding: 22px 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.login-brand-logo {
  height: 32px;
  filter: brightness(0) invert(1);
}

.login-brand-tagline {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  font-family: var(--font-primary);
  margin: 0;
  letter-spacing: 0.01em;
}

/* ── Form Body ── */
.login-form-body {
  padding: 24px 24px 20px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  line-height: 1.3;
}

.subtitle {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 18px;
  font-family: var(--font-primary);
}

label {
  font-size: 13px;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
}

input[type="email"],
input[type="password"],
input[type="text"] {
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  margin-bottom: 14px;
  font-size: 14px;
  font-family: var(--font-primary);
  background: #f8faff;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  outline: none;
  color: var(--color-text-primary);
}

input[type="email"]:focus,
input[type="password"]:focus,
input[type="text"]:focus {
  border-color: #3467d5;
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.12);
  background: #fff;
}

/* ── Password Wrapper ── */
.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 42px;
  margin-bottom: 14px;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-65%);
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.toggle-password:hover {
  color: #3467d5;
}

/* ── Links Row ── */
.login-links {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  margin: 0;
}

.remember-label input[type="checkbox"] {
  width: 15px;
  height: 15px;
  margin: 0;
  padding: 0;
  accent-color: #3467d5;
  border-radius: 4px;
  cursor: pointer;
}

.forgot {
  color: #3467d5;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  font-family: var(--font-primary);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s;
}

.forgot:hover {
  color: #1d3fa8;
  text-decoration: underline;
}

/* ── Sign In Button ── */
.btn-signin {
  width: 100%;
  padding: 11px 18px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #1d3fa8 0%, #3467d5 60%, #5b87f0 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: var(--font-primary);
  cursor: pointer;
  margin-bottom: 16px;
  transition: opacity 0.2s, transform 0.1s;
  letter-spacing: 0.01em;
}

.btn-signin:hover {
  opacity: 0.92;
}

.btn-signin:active {
  transform: scale(0.99);
}

/* ── Busy / disabled states ── */
.btn-signin:disabled,
.btn-signin:disabled:hover {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.btn-spinner {
  width: 15px;
  height: 15px;
  border: 2.5px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  vertical-align: -2px;
  margin-right: 8px;
  animation: btn-spin 0.7s linear infinite;
}

/* Used inside the white circular social buttons, where a white ring is invisible */
.btn-spinner-dark {
  width: 22px;
  height: 22px;
  margin-right: 0;
  border-color: rgba(52, 103, 213, 0.25);
  border-top-color: #3467d5;
}

@keyframes btn-spin { to { transform: rotate(360deg); } }

/* ── Divider ── */
.divider {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  color: #94a3b8;
  font-size: 12px;
  font-family: var(--font-primary);
}

.divider span {
  flex: 1;
  height: 1px;
  background: #e9eef6;
}

.divider p {
  margin: 0 10px;
  white-space: nowrap;
}

/* ── Social Icon Buttons ── */
.social-icons-row {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.btn-social-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
}

.btn-social-icon:hover {
  border-color: #c7d2e8;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transform: translateY(-1px);
}

.btn-social-icon:disabled,
.btn-social-icon:disabled:hover {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  border-color: #e2e8f0;
}

.btn-google-icon { background: #fff; }
.btn-facebook-icon { background: #fff; }
.btn-email-icon { background: #fff; }

/* ── Switch Box ── */
.switch-box {
  display: flex;
  justify-content: center;
  gap: 5px;
  font-size: 13px;
  font-family: var(--font-primary);
  color: var(--color-text-secondary);
}
</style>
